import { useState } from 'react';
import { usePathname, useRouter, useSearchParams } from '@/lib/react-router-compat';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ArrowLeft, Pencil } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import Link from '@/components/core/router-link';
import { BREADCRUMB_CUSTOM_TITLES } from '@/constant/breadcrumb-custom-title';
import PageBreadcrumb from '@/components/breadcrumb/breadcrumb';
import {
  useGetToolDetails,
  useToolAssociatedAgentCount,
} from '@/modules/gpt-chats/hooks/use-tools';

import { EditMcpServerModal } from '@/modules/gpt-chats/components/tools/edit-mcp-server-modal';
import { ToolStatus } from '@/modules/gpt-chats/types/tools.service.type';
import { Sparkles as CircleSpark } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const McpServerDetailsLayout = ({
  children,
  mcpServerId,
}: {
  children: React.ReactNode;
  mcpServerId: string;
}) => {
  const pathname = usePathname();
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const copiedBlocksTool = searchParams.get('copiedBlocksTool') === 'true';
  const { data: toolDetails, isLoading } = useGetToolDetails(mcpServerId, isBlocksTool);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { data: associatedAgentCount } = useToolAssociatedAgentCount(mcpServerId, !isBlocksTool);

  const getCurrentTab = () => {
    if (pathname.includes('/configuration')) return 'configuration';
    if (pathname.includes('/tutorial-guide')) return 'tutorial-guide';
    // if (pathname.includes("/dashboard")) return "dashboard";
    return 'configuration';
  };

  const handleTabChange = (value: string) => {
    router.push(
      `/ai/mcp-server-details/${mcpServerId}/${value}?isBlocksTool=${isBlocksTool}&copiedBlocksTool=${copiedBlocksTool}`
    );
  };

  const mcpServerName = toolDetails?.Name || t('MCP_SERVER');

  BREADCRUMB_CUSTOM_TITLES[`/ai/mcp-server-details/${mcpServerId}?isBlocksTool=${isBlocksTool}`] =
    'MCP Servers';
  return (
    <div className="flex flex-col gap-6">
      <PageBreadcrumb breadcrumbIndex={3} />

      <div className="flex items-center gap-4">
        <Link
          href={`/ai/tools?toolType=${isBlocksTool && !copiedBlocksTool ? 'blocks-tools' : 'mcp-server'}`}
        >
          <Button variant="ghost" size="icon" className="-ml-3 hover:bg-transparent">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{isLoading ? t('LOADING') : mcpServerName}</h1>
          <Button
            variant="ghost"
            size="icon"
            className="text-primary"
            onClick={() => setIsEditModalOpen(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex w-full justify-between">
        <Tabs value={getCurrentTab()} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="configuration">{t('CONFIGURATION')}</TabsTrigger>
            <TabsTrigger value="tutorial-guide">{t('TUTORIAL_GUIDE')}</TabsTrigger>
            {/* <TabsTrigger value="dashboard">Dashboard</TabsTrigger> */}
          </TabsList>
        </Tabs>
        {!isBlocksTool && (
          <div className="m-2 hidden flex-row items-center gap-2 rounded-sm bg-border px-2 py-1.5 lg:flex">
            <CircleSpark className="aspect-square h-3 w-3 p-0 text-cyan-700" />
            <span className="p-0 text-sm text-medium-emphasis">{t('ASSOCIATED_AGENTS')} </span>
            <span className="p-0 text-sm text-high-emphasis">
              {associatedAgentCount?.data ?? 0}
            </span>
          </div>
        )}
      </div>

      <div className="mb-2">{children}</div>

      <EditMcpServerModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        tool={
          toolDetails
            ? {
                id: mcpServerId,
                name: toolDetails.Name,
                url: '',
                description: toolDetails.Description,
                status: toolDetails.Status as ToolStatus,
              }
            : undefined
        }
        isBlocksTool={isBlocksTool}
        copiedBlocksTool={copiedBlocksTool}
      />
    </div>
  );
};
